function dashNumber(value, fallback = 0) {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

function dashPlural(n, singular, plural) {
  return Number(n) === 1 ? singular : (plural || singular + 's');
}

function dashboardPageDesc({ isLive, totalBuilds, runningCount, needsReviewCount, passRate, verdicted }) {
  if (!isLive) return import.meta.env?.VITE_MOCK_AUTH === '1'
    ? '6 builds running across the workspace · pass rate holding at 91% this month.'
    : 'Workspace statistics unavailable.';

  const totalLabel = totalBuilds > 0
    ? `${totalBuilds} ${dashPlural(totalBuilds, 'build')} total`
    : 'No builds yet';
  const activity = [];
  if (runningCount > 0) activity.push(`${runningCount} running`);
  if (needsReviewCount > 0) activity.push(`${needsReviewCount} waiting on review`);
  if (!activity.length) activity.push(totalBuilds > 0 ? 'no active builds' : 'ready for the first build');

  const quality = verdicted > 0
    ? `${passRate}% pass rate across ${verdicted} ${dashPlural(verdicted, 'verdict')}`
    : 'no reviewed builds yet';

  return `${totalLabel} · ${activity.join(' · ')} · ${quality}.`;
}

function Dashboard({ go, openJob }) {
  const { jobs } = window.MAV;
  const reviewJobs = jobs.filter(j => j.status === 'needs_review');
  const recent = jobs;
  // RBAC-07 GAP-5: org / "all my teams" context → show the workspace each build
  // belongs to. useViewer re-renders on a workspace switch. Single tenant → no column.
  if (window.RBAC && window.RBAC.useViewer) window.RBAC.useViewer();
  const aggregate = !!(window.RBAC && window.RBAC.isAggregateView && window.RBAC.isAggregateView());
  // VD-01: on a codex workspace the $ meters are nonsense (codex is sub-$0), so the Token-spend
  // tile + Cost-by-stage card swap for the codex quota surfaces. Non-codex keeps the $ meters.
  const codexView = !!(window.RBAC && window.RBAC.activeEngine && window.RBAC.activeEngine() === 'codex');

  // Live OpenRouter account usage — Phoenix's system (usage.jsx). Fetched on mount
  // + cached on MAV.credits (hydrate doesn't populate it); drives the Token-spend
  // tile so the Dashboard matches Usage & budgets + the Admin desk.
  const [credits, setCredits] = React.useState(window.MAV.credits || null);
  React.useEffect(() => {
    let alive = true;
    if (window.GooseAPI && window.GooseAPI.openrouterCredits) {
      window.GooseAPI.openrouterCredits().then((d) => { if (alive && d) { window.MAV.credits = d; setCredits(d); } }).catch(() => {});
    }
    return () => { alive = false; };
  }, []);

  // Refresh codex usage on dashboard mount + on tab focus, so SPA-nav here (or returning to the
  // tab) reads the latest auto-polled sidecar — no full page reload needed. Cheap endpoint
  // re-fetch of the 15-min-fresh sidecar, NOT a live codex poll.
  React.useEffect(() => {
    const refresh = () => { if (window.MAV && window.MAV.refreshCodexUsage) window.MAV.refreshCodexUsage(); };
    refresh();
    window.addEventListener('focus', refresh);
    return () => window.removeEventListener('focus', refresh);
  }, []);

  const spark = [40, 52, 38, 61, 70, 48, 80, 66, 90, 72, 84, 95];

  // Live workspace stats from /api/data/summary (hydrate.js populates MAV.summary).
  // Mock defaults preserved so the demo renders without a server.
  const s = window.MAV.summary || {};
  const demo = import.meta.env?.VITE_MOCK_AUTH === '1';
  const isLive = window.MAV.__live === true && window.MAV.summary != null;
  const totalBuilds = (s.total_builds != null) ? dashNumber(s.total_builds) : demo ? 128 : '—';
  const runningCount = (s.running != null) ? dashNumber(s.running) : demo ? 6 : null;
  const needsReviewCount = (s.needs_review != null) ? dashNumber(s.needs_review, reviewJobs.length) : reviewJobs.length;
  const passed = (s.passed != null) ? dashNumber(s.passed, 0) : null;
  // Pass rate = PASS / builds that actually got a verdict. Counting still-running
  // or queued builds in the denominator understates a healthy pipeline, so we
  // divide by `verdicted` (PASS+CONDITIONAL+FAIL), not by every build.
  const verdicted = (s.verdicted != null) ? dashNumber(s.verdicted, 0) : null;
  const passRate = (passed != null && verdicted != null && verdicted > 0)
    ? Math.round((passed / verdicted) * 100)
    : demo ? 91 : '—';
  // Token spend = Phoenix's system (usage.jsx:45): real OpenRouter account usage
  // when the key is live, else our DB build-cost sum. Keeps the Dashboard tile
  // consistent with Usage & budgets + the Admin desk — all show the real OR spend,
  // with the DB total as the graceful fallback when the OR key flaps.
  const _orLive = !!(credits && credits.available && credits.remaining != null);
  const totalCost = _orLive
    ? Number(credits.total_usage)
    : ((s.total_cost != null) ? dashNumber(s.total_cost) : demo ? 340 : null);
  const costLabel = totalCost == null ? '—' : '$' + (totalCost >= 100 ? Math.round(totalCost) : totalCost.toFixed(2));

  // Avg build time = median of per-build total elapsed (SUM of stage elapsed_s),
  // from /api/data/metrics → MAV.metrics.buildTime. This is the SAME field the
  // Avg-build-time detail screen shows as "Median build", so the tile and the
  // drill-down agree. The tile was previously hardcoded to the prototype's "2.7"
  // mock (never wired). Mock fallback kept for older server builds / no timed data.
  const _bt = (window.MAV.metrics && window.MAV.metrics.buildTime && Number(window.MAV.metrics.buildTime.builds) > 0)
    ? window.MAV.metrics.buildTime : null;
  const buildMin = _bt ? (Number(_bt.median_s) / 60).toFixed(1) : demo ? '2.7' : '—';
  const buildSub = _bt ? `${_bt.builds} ${dashPlural(_bt.builds, 'build')} · median` : demo ? '6 stages · median' : 'No timed builds';

  const pageDesc = dashboardPageDesc({ isLive, totalBuilds, runningCount, needsReviewCount, passRate, verdicted });

  // Live greeting: time-of-day + the signed-in user's first name (from
  // MAV.currentUser, populated by hydrate's /me fetch). Falls back to "there"
  // when no user (demo / pre-hydrate), and to "Jordan" as the original mock
  // copy only when MAV is entirely absent (impossible in practice but keeps
  // the prototype's offline-demo behavior intact).
  const greetingHour = (new Date()).getHours();
  const timeOfDay = greetingHour < 12 ? 'morning' : greetingHour < 18 ? 'afternoon' : 'evening';
  const cu = window.MAV && window.MAV.currentUser;
  const firstName = cu && cu.name ? String(cu.name).trim().split(/\s+/)[0] : (window.MAV ? 'there' : 'Jordan');
  const greeting = `Good ${timeOfDay}, ${firstName}`;
  return (
    <div className="page fade-in">
      <div className="page-head">
        <div>
          <h1 className="page-title" title="Talk to me, Goose." data-greeting={cu ? 'live' : 'mock'}>{greeting}</h1>
          <p
            className="page-desc"
            data-live={isLive ? '1' : '0'}
            data-total-builds={totalBuilds}
            data-running={runningCount}
            data-needs-review={needsReviewCount}
            data-pass-rate={passRate}
            data-verdicted={verdicted == null ? '' : verdicted}
          >{pageDesc}</p>
        </div>
        <button className="btn btn-primary btn-lg" onClick={() => go('new')}><I.add size={17} /> New build</button>
      </div>

      {/* Stats */}
      <div className="stat-grid">
        <div className="stat clickable" title="Sorties flown this month." onClick={() => go('m_builds')} data-stat="builds">
          <span className="stat-go"><I.arrow size={16} /></span>
          <div className="k"><I.builds size={14} /> Builds {isLive ? 'total' : 'this month'}</div>
          <div className="v">{totalBuilds}</div>
          <div className="sub">{isLive ? 'workspace lifetime' : demo ? '▲ 18% vs April' : 'Unavailable'}</div>
        </div>
        <div className="stat clickable" title="Your wingman's got your six." onClick={() => go('m_pass')} data-stat="pass">
          <span className="stat-go"><I.arrow size={16} /></span>
          <div className="k"><I.check size={14} /> Pass rate</div>
          <div className="v">{passRate}<small>%</small></div>
          {demo ? <div className="spark">{spark.map((h, i) => <i key={i} style={{ height: h + '%' }}></i>)}</div> : <div className="sub">{verdicted > 0 ? `${verdicted} reviewed builds` : 'No reviewed builds'}</div>}
        </div>
        <div className="stat clickable" title="I feel the need — the need for speed." onClick={() => go('m_time')} data-stat="time" data-live-time={_bt ? '1' : '0'}>
          <span className="stat-go"><I.arrow size={16} /></span>
          <div className="k"><I.clock size={14} /> Avg build time</div>
          <div className="v">{buildMin}<small>min</small></div>
          <div className="sub">{buildSub}</div>
        </div>
        {codexView
          ? <CodexSessionTile go={go} />
          : <div className="stat clickable" title="Mind the fuel gauge, Maverick." onClick={() => go('m_spend')} data-stat="spend">
              <span className="stat-go"><I.arrow size={16} /></span>
              <div className="k"><I.coins size={14} /> Token spend</div>
              <div className="v">{costLabel}</div>
              {demo && <div className="meter" style={{ marginTop: 12 }}><span style={{ width: '68%' }}></span></div>}
            </div>}
      </div>

      <div className="grid-2-1 mt-24">
        {/* Recent builds */}
        <div className="card card-pad tbl-scroll">
          <div className="sec-head">
            <span className="sec-title">Recent builds</span>
            <button className="sec-link" onClick={() => go('builds')}>View all <I.chevron size={14} /></button>
          </div>
          <table className="tbl">
            <thead><tr><th>Build</th>{aggregate && <th>Workspace</th>}<th>Client</th><th>Source</th><th>Status</th><th>Tokens</th></tr></thead>
            <tbody>
              {recent.map(j => {
                const c = window.MAV.clientBySlug(j.client) || { name: j._org ? '—' : 'Unassigned' };
                return (
                  <tr key={j.id} className="clickable" onClick={() => openJob(j.id)}>
                    <td><div className="nm">{(j.name || '').length > 26 ? j.name.slice(0, 26) + '…' : (j.name || j.id)}</div><div className="mono" style={{ fontSize: 11 }}>{j.id}</div></td>
                    {aggregate && <td>{window.WorkspaceTag ? <WorkspaceTag job={j} /> : null}</td>}
                    <td><div className="row gap-8"><ClientBadge slug={j.client} size={24} /><span>{c.name}</span></div></td>
                    <td><div className="row gap-6" style={{ flexWrap: 'wrap' }}><span className="chip">{j.source ? j.source.toUpperCase() : '—'}</span>{(() => {
                      // Build-engine tag (PY-10 P3) — same pattern as builds.jsx:143.
                      // Shown ONLY when job.engine is known; unset → no tag (never
                      // mislabels a legacy build as Codex). router→Bullseye, legacy→Swarm.
                      const e = j.engine; if (!e) return null;
                      const L = { codex: 'Codex', bullseye: 'Bullseye', router: 'Bullseye', legacy: 'Swarm', claude: 'Claude' }[e] || e;
                      return <span className={'eng-tag eng-' + e} title={'Build engine: ' + L}>{L}</span>;
                    })()}</div></td>
                    <td><StatusPill status={j.status} verdict={j.verdict} /></td>
                    <td className="mono">{j.tokensK != null ? j.tokensK + 'K' : '—'} · ${(j.cost ?? 0).toFixed(2)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Needs review */}
        <div className="col gap-20">
          <div className="card card-pad">
            <div className="sec-head">
              <span className="sec-title">Needs your review</span>
              <span className="nav-badge">{reviewJobs.length}</span>
            </div>
            {reviewJobs.map(j => {
              const c = window.MAV.clientBySlug(j.client);
              return (
                <div key={j.id} className="nr-item" onClick={() => openJob(j.id, 'review')}>
                  <div className="nr-stripe" style={{ background: 'var(--cond)' }}></div>
                  <ClientBadge slug={j.client} size={32} />
                  <div className="grow" style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{j.name}</div>
                    <div className="muted" style={{ fontSize: 11.5 }}>{c.name} · CONDITIONAL · {j.started}</div>
                  </div>
                  <I.arrow size={16} />
                </div>
              );
            })}
            {reviewJobs.length === 0 && <p className="muted" style={{ fontSize: 13 }}>Clear skies — nothing waiting on you.</p>}
          </div>

          {codexView ? <CodexUsagePanel /> : (() => {
            // Cost-by-stage: live when hydrate populated MAV.costByStage; mock
            // otherwise. Empty stages ($0 — e.g. QA which is multi-tool) are
            // hidden so the panel doesn't show a zero-width bar that looks like
            // a bug. Bars are colored per the prototype's palette by index.
            const live = window.MAV.costByStage && Array.isArray(window.MAV.costByStage.rows)
              ? window.MAV.costByStage : null;
            const isLiveCBS = !!live;
            const COLORS = ['var(--brand-orange)', 'var(--brand-amber)', 'var(--brand-yellow)', 'var(--queued)', 'var(--running)', 'var(--cond)'];
            let rows;
            if (isLiveCBS) {
              const positive = live.rows.filter((r) => Number(r.cost) > 0);
              const total = positive.reduce((a, r) => a + Number(r.cost), 0) || 1;
              // Sort by cost descending so the biggest spends lead
              rows = [...positive]
                .sort((a, b) => Number(b.cost) - Number(a.cost))
                .map((r, i) => ({
                  label: r.name,
                  cost: Number(r.cost),
                  pct: Math.round((Number(r.cost) / total) * 100),
                  color: COLORS[i % COLORS.length],
                }));
            } else if (demo) {
              rows = [
                { label: 'Semantic (Sonnet)',  pct: 42, color: 'var(--brand-orange)' },
                { label: 'Execution (Gemini)', pct: 28, color: 'var(--brand-amber)' },
                { label: 'QA (Perceptron)',    pct: 18, color: 'var(--brand-yellow)' },
                { label: 'Planning · Approval', pct: 12, color: 'var(--queued)' },
              ];
            } else { rows = []; }
            return (
              <div className="card card-pad" data-cost-by-stage={isLiveCBS ? 'live' : demo ? 'mock' : 'unavailable'}>
                <div className="sec-head">
                  <span className="sec-title">Cost by stage</span>
                  {isLiveCBS && <span className="mono muted" style={{ fontSize: 11 }}>${live.totalCost.toFixed(2)} lifetime</span>}
                </div>
                {!rows.length && <p className="muted">{isLiveCBS ? 'No stage costs recorded.' : 'Stage costs unavailable.'}</p>}
                {rows.map((r, i) => (
                  <div key={i} style={{ marginBottom: 12 }} data-cbs-row={r.label}>
                    <div className="row between" style={{ fontSize: 12, marginBottom: 5 }}>
                      <span>{r.label}</span>
                      <span className="mono muted">{isLiveCBS ? `$${r.cost.toFixed(2)} · ${r.pct}%` : `${r.pct}%`}</span>
                    </div>
                    <div className="meter" style={{ margin: 0 }}><span style={{ width: r.pct + '%', background: r.color }}></span></div>
                  </div>
                ))}
              </div>
            );
          })()}
        </div>
      </div>
    </div>
  );
}
window.Dashboard = Dashboard;
